import { PropsWithChildren, useEffect, useMemo, useState } from "react";

import { useCurrentExtranet } from "@/features/admin/hooks/queries";
import { useIsAdminRoute } from "@/features/shared/navigation/use-is-admin-route";
import i18n from "@/lib/i18n";
import { LocaleContext, LocaleDebugContext, useLocale } from "@/lib/locale/locale-context";
import { AppLocale, resolveEffectiveLocale } from "@/lib/locale/resolve-effective-locale";

type LocaleOverrideProviderProps = PropsWithChildren<{
  locale: AppLocale;
}>;

/**
 * Forces a fixed locale for the subtree (e.g. MaritimeForecast always NL).
 * Syncs i18n on mount and restores the parent locale on unmount.
 */
export function LocaleOverrideProvider({ locale, children }: LocaleOverrideProviderProps) {
  const parentLocale = useLocale();

  useEffect(() => {
    void i18n.changeLanguage(locale);
    return () => {
      void i18n.changeLanguage(parentLocale);
    };
  }, [locale, parentLocale]);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function LocaleProvider({ children }: PropsWithChildren) {
  const [debugOverride, setDebugOverride] = useState<AppLocale | null>(null);
  const debugControls = useMemo(
    () => ({ override: debugOverride, setOverride: setDebugOverride }),
    [debugOverride],
  );

  const isAdminRoute = useIsAdminRoute();
  const { data: extranet } = useCurrentExtranet();

  const effective = useMemo(
    () => debugOverride ?? resolveEffectiveLocale(isAdminRoute, extranet?.locale),
    [debugOverride, isAdminRoute, extranet?.locale],
  );

  useEffect(() => {
    if (i18n.language !== effective) {
      void i18n.changeLanguage(effective);
    }
  }, [effective]);

  return (
    <LocaleDebugContext.Provider value={debugControls}>
      <LocaleContext.Provider value={effective}>{children}</LocaleContext.Provider>
    </LocaleDebugContext.Provider>
  );
}
